import { useCallback, useEffect, useRef, useState } from 'react';
import { translate, type MessageKey, type WashedLocale } from '@washed/i18n';
import { WAITLIST_ERROR_FIELD, type WaitlistErrorCode } from '../api/waitlist.js';
import type { FormState } from '../routes/HomePage.js';
import { TURNSTILE_ENABLED, type TurnstileHandle } from './TurnstileWidget.js';

const ERROR_COPY: Record<WaitlistErrorCode, MessageKey> = {
  invalid_phone: 'marketing.form.error.phone',
  invalid_email: 'marketing.form.error.email',
  consent_required: 'marketing.form.error.consent',
  challenge_failed: 'marketing.form.error.challenge',
  network: 'marketing.form.error.network',
  server: 'marketing.form.error.server',
};

/**
 * Shared submit lifecycle for the waitlist forms (homepage Closing, washer
 * application). Owns what both forms must get right for a visitor to recover
 * from a failed submit:
 *
 *  - the Turnstile token: single-use, so it is reset after every failure, and
 *    submit waits until a fresh one exists (unless the widget itself failed, in
 *    which case the server's answer explains it);
 *  - which message to show, inline on the offending field or for the whole form;
 *  - focus: to the field to fix on a field error, to the confirmation on success.
 */
export function useWaitlistForm(state: FormState, locale: WashedLocale) {
  const turnstileRef = useRef<TurnstileHandle>(null);
  const phoneRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const successRef = useRef<HTMLParagraphElement>(null);
  const [token, setToken] = useState('');
  const [widgetFailed, setWidgetFailed] = useState(false);
  const [error, setError] = useState<WaitlistErrorCode | null>(null);

  useEffect(() => {
    if (state.kind === 'loading') {
      setError(null);
    } else if (state.kind === 'error') {
      setError(state.code);
      turnstileRef.current?.reset();
      const field = WAITLIST_ERROR_FIELD[state.code];
      if (field === 'phone') phoneRef.current?.focus();
      if (field === 'email') emailRef.current?.focus();
    } else if (state.kind === 'success') {
      successRef.current?.focus();
    }
  }, [state]);

  const onToken = useCallback((next: string) => {
    setToken(next);
    if (next) setWidgetFailed(false);
  }, []);
  const onFailure = useCallback(() => setWidgetFailed(true), []);

  /** Drop a field's error as soon as the visitor edits that field. */
  const clearErrorFor = useCallback((field: 'phone' | 'email') => {
    setError((current) =>
      current !== null && WAITLIST_ERROR_FIELD[current] === field ? null : current,
    );
  }, []);

  const message = (code: WaitlistErrorCode) => translate(ERROR_COPY[code], locale);
  const errorField = error === null ? undefined : WAITLIST_ERROR_FIELD[error];

  return {
    token,
    turnstile: { ref: turnstileRef, onToken, onFailure },
    awaitingChallenge: TURNSTILE_ENABLED && !token && !widgetFailed,
    phoneRef,
    emailRef,
    successRef,
    clearErrorFor,
    phoneError: errorField === 'phone' && error ? message(error) : undefined,
    emailError: errorField === 'email' && error ? message(error) : undefined,
    formError: error !== null && errorField === undefined ? message(error) : undefined,
  };
}
